<template>
  <div
    class="product-gallery"
    :class="{ 'is-loading': isLoading }"
  >
    <div
      v-if="isLoading || !productMedia"
      class="product-gallery__image-list"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="product-gallery__media product-gallery__image-wrapper"
      />
    </div>

    <div
      v-else
      ref="imageList"
      class="product-gallery__image-list"
      @scroll.passive="handleSliderScroll"
    >
      <template
        v-for="(mediaItem, index) in productMedia"
        :key="mediaItem.id"
      >
        <!--
          Video element
        -->
        <div
          v-if="mediaItem?.isVideo"
          class="product-gallery__media product-gallery__video-wrapper"
        >
          <video-element
            :video="mediaItem"
            @video-error="handleMediaError(mediaItem.id)"
          />
        </div>

        <!--
          Image wrapper
          - Uses a button element to act as a trigger for the Fancybox modal
          - The `sizes`, `src`, and `srcset` attributes are used for the image
            loaded in the Fancybox modal
          - The sizes attribute is set based on the device's aspect ratio
          - On devices taller than the default aspect ratio, the image width
            is equal to 100% of the viewport width
          - On devices wider than the default aspect ratio, the image width
            is equal to the viewport height divided by the image aspect ratio
          - These sizes are doubled to select images suitable for zooming in
        -->
        <button
          v-else
          :aria-label="lightboxToggleLabel"
          class="product-gallery__media product-gallery__image-wrapper"
          data-fancybox="product-gallery"
          data-sizes="(max-aspect-ratio: 5/7) 200vw, 146vh"
          :data-src="getFancyboxImageSrc(mediaItem)"
          :data-srcset="getSrcset(mediaItem)"
        >
          <!--
            Responsive image component
            - The first two images will always use 'eager' loading
            - Following images use a computed property for the loading attribute
          -->
          <responsive-image
            class="product-gallery__image"
            :image="mediaItem"
            :fetch-priority="index < 2 ? 'high' : 'auto'"
            :loading="index < 2 ? 'eager' : loadAttribute"
            :sizes="getGalleryImageSizes(index)"
          />
        </button>
      </template>
    </div>

    <a
      v-if="showStlLink"
      class="product-gallery__stl-link"
      href="#shop-the-look"
      :text="stlLabel"
    />

    <div
      v-if="showDots"
      class="product-gallery__dots"
      role="presentation"
    >
      <span
        v-for="(item, index) in productMedia"
        :key="index"
        class="product-gallery__dot"
        :class="{ 'is-active': index === activeSlide }"
      />
    </div>
  </div>
</template>

<script>

/**
 * Component: Product gallery
 * -----------------------------------------------------------------------------
 * @param {boolean} isMobile - If the customer is on mobile, from Pinia.
 * @param {string} parentState - Component state of the parent component.
 * @param {boolean} showStlBtn - Display the 'Shop the look' button.
 * @param {object} product - Product data object.
 */

/**
 * This specific import is required to ensure the package is included when
 *   Webpack is run in production mode.
 */

import { Fancybox } from '@fancyapps/ui/dist/fancybox/fancybox.esm.js'

import breakpoints from '~/helpers/breakpoints'
import {
  getCloudinarySizedImageUrl,
  getImageSrcset,
  getSizedImageUrl,
} from '~/helpers/format'

import ResponsiveImage from '~components/responsive-image/responsive-image'
import VideoElement from '~components/video-element/video-element'

export default {
  name: 'ProductGallery',

  components: {
    ResponsiveImage,
    VideoElement,
  },

  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    parentState: {
      type: String,
      default: 'loading',
    },
    product: {
      type: [ Boolean, Object ],
      default: false,
    },
    showStlButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeSlide: 0,
      productMedia: [],
      userHasScrolled: false,
    }
  },

  computed: {

    /**
     * Whether the parent component is in its loading state.
     * @returns {boolean}
     */
    isLoading() {
      return this.parentState !== 'ready'
    },

    /**
     * Whether gallery images should be lazy loaded.
     * @param {number} index - Image index.
     */
    loadAttribute() {

      /**
       * If a user is browsing from a mobile device and has interacted with the
       * gallery, allow all images to load.
       */
      if (this.isMobile && this.userHasScrolled) {
        return 'eager'
      }

      return 'lazy'
    },

    /**
     * Whether the dots indicating image position should be displayed.
     * @returns {boolean}
     */
    showDots() {
      return this.parentState === 'ready' && this.product.images.length > 1
    },

    /**
     * Whether the shop the look link should be displayed.
     * @returns {boolean}
     */
    showStlLink() {
      return this.showStlButton && this.stlLabel
    },

    /**
     * Get the label for the shop the look link from theme strings.
     * @returns {string}
     */
    stlLabel() {
      return mint.strings.productDetailPage.gallery.shopTheLookLink
    },

    /**
     * Get the label for the lightbox toggle elements.
     * @returns {string}
     */
    lightboxToggleLabel() {
      return mint.strings.productDetailPage.gallery.lightboxToggleLabel
    },
  },

  watch: {

    /**
     * Rebind the Fancybox handler if the product images change.
     * - Runs on mount and subsequent product changes.
     */
    'product.images': {
      handler: function() {
        this.productMedia = this.getProductMedia()
        this.bindFancybox()
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Set up the Fancybox component with options.
     */
    bindFancybox() {
      if (
        !this.product.images ||
        !this.product.images?.length
      ) {
        return
      }

      const productHasImages = this.product.images.find((image) => {
        return Boolean(image.src)
      })

      if (!productHasImages) {
        return
      }

      Fancybox.bind('[data-fancybox="product-gallery"]', {
        Carousel: { infinite: false },
        Hash: false,
        id: 'product-gallery',
        l10n: mint.shop?.language ?? 'en',
        on: {
          'close': (fancybox) => {
            this.setSlide(fancybox.carousel.page)
          },
        },
        Thumbs: false,
      })
    },

    /**
     * Generate the 'sizes' attribute for a gallery image.
     * @param {number} index - Index of the image within the gallery
     */
    getGalleryImageSizes(index) {
      const largeWidth = index > 3 ? '16vw' : '33vw'

      return `(min-width: ${breakpoints.large}) ${largeWidth}, (min-width: ${breakpoints.medium}) 50vw, 100vw`
    },

    /**
     * Generate fallback image URL for the Fancybox image.
     * @param {object} image - Image data object
     */
    getFancyboxImageSrc(image) {
      if (!image.src) {
        return ''
      }

      if (image.source == 'cloudinary') {
        return getCloudinarySizedImageUrl({
          baseUrl: image.baseUrl,
          publicId: image.publicId,
          transformations: [
            'q_auto',
            'f_auto',
            'c_scale',
            'w_800',
          ],
        })
      }

      return getSizedImageUrl(image.src, '800x')
    },

    /**
     * Get the `srcset` attribute for a given image.
     * @param {object} image - Image data object
     */
    getSrcset(image) {
      if (!image.src) {
        return ''
      }

      return getImageSrcset(image).srcset
    },

    /**
     * Generate an array of product images and videos.
     * @returns {Array}
     */
    getProductMedia() {
      const media = [ ...this?.product?.images ]
      const productVideos = this.product?.videos?.pdpGallery

      if (!productVideos || productVideos?.length === 0) {
        return media
      }

      for (const video of productVideos) {
        const { position = 2 } = video
        const zeroIndexedPosition = Math.max(position - 1, 0)

        media.splice(
          zeroIndexedPosition,
          0,
          {
            isVideo: true,
            ...video,
          },
        )
      }

      return media
    },

    /**
     * Handle scroll events when the image gallery is in slider mode.
     * @param {Event} event - Scroll event object.
     */
    handleSliderScroll(event) {
      if (!this.isMobile) {
        return
      }

      this.userHasScrolled = true

      /**
       * Compare the current scroll position against the width of the slider.
       * - Divide the scroll position by the slider's width
       * - This will result in a value between 0 and the number of slides
       * - The active slide is updated when a slide is at least 50% in view
       */
      const scrollPosition = event.target.scrollLeft
      const slideWidth = event.target.children[0].offsetWidth
      const slidePosition = scrollPosition / slideWidth

      this.activeSlide = Math.round(slidePosition)
    },

    /**
     * Remove media with errors, e.g. 404s or format incompatibilities.
     * - The media ID is used to handle situations where multiple media items
     *   fail simultaneously, causing array indices to overlap.
     * @param {string} mediaId
     */
    handleMediaError(mediaId = '') {
      const matchingIndex = this?.productMedia?.findIndex(
        (item) => item.id === mediaId,
      )

      if (matchingIndex === -1 || matchingIndex === undefined) {
        mint.Log('warning', `Could not find media item with ID ${id}`)
        
        return
      }

      this.productMedia.splice(matchingIndex, 1)
    },

    /**
     * Set the slider position from a slide index.
     * - The slider will only be scrolled if the target slide is not visible.
     * @param {number} index - Target slide index to be scrolled into view.
     */
    setSlide(index = 0) {
      const slider = this.$refs.imageList
      const targetSlide = slider.children[index]

      if (
        slider.scrollLeft <= targetSlide.offsetLeft &&
        targetSlide.offsetLeft <= slider.scrollLeft + slider.offsetWidth
      ) {
        return
      }

      slider.scrollLeft = targetSlide.offsetLeft
    },
  },
}
</script>
